import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { DEFAULT_LANGUAGE } from '../../config/languages'
import { isLanguage } from '../../utils/languages'

export default function LanguageRouter({ children }: { children?: React.ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { i18n } = useTranslation()

  useEffect(() => {
    const path = location.pathname

    // Do not redirect special routes (like /reset-password)
    if (path === '/reset-password') {
      return
    }

    // Check for invalid double language prefixes (e.g., /el/en/)
    const segments = path.split('/').filter(Boolean)
    if (segments.length >= 2 && isLanguage(segments[0]) && isLanguage(segments[1])) {
      // We have a double language prefix, redirect to the correct format
      const remainingPath = segments.slice(2).join('/')
      const correctPath = `/${segments[0]}/${remainingPath}`
      navigate(correctPath, { replace: true })
      return
    }

    // Check if the path already starts with a valid language code
    const langMatch = path.match(/^\/([a-z]{2})(\/|$)/)

    if (langMatch && isLanguage(langMatch[1])) {
      // Path already has a valid language code, just sync i18n
      i18n.changeLanguage(langMatch[1])
      return
    }

    // Get detected or default language
    let detectedLang = i18n.language

    // If detected language is not supported, default to English
    if (!isLanguage(detectedLang)) {
      detectedLang = DEFAULT_LANGUAGE
    }

    // For paths without language prefix, add the language prefix
    const newPath = `/${detectedLang}${path}`
    navigate(newPath, { replace: true })
  }, [location.pathname, navigate, i18n])

  return <>{children}</>
}
